import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, Box, IconButton,
  Drawer, List, ListItem, ListItemText,
  useMediaQuery, useTheme, Container, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assets/logo.png';
import { keyframes } from '@mui/system';

const shimmer = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Home',     path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact',  path: '/contact' },
  ];

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          background: '#ffffff',
          backdropFilter: 'blur(20px)',
          borderBottom: scrolled ? '1px solid rgba(13,71,161,0.12)' : '1px solid rgba(13,71,161,0.06)',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            {/* Logo — fully clickable */}
            <Box
              onClick={handleLogoClick}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                textDecoration: 'none', cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.03)' },
              }}
            >
              <img
                src={Logo}
                style={{ height: '52px', objectFit: 'contain', pointerEvents: 'none' }}
                alt="Quantum IT AI Logo"
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1, pointerEvents: 'none' }}>
                <Typography sx={{
                  fontFamily: '"Rajdhani", "Inter", sans-serif',
                  fontWeight: 800, fontSize: '1.5rem', letterSpacing: '0.08em',
                  background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                  backgroundClip: 'text', WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent', lineHeight: 1.1,
                }}>
                  QuantumITAI
                </Typography>
                <Typography sx={{
                  fontFamily: '"Inter", sans-serif', fontWeight: 500,
                  fontSize: '0.62rem', letterSpacing: '0.15em',
                  color: '#546e7a', textTransform: 'uppercase',
                }}>
                  Technology Solutions
                </Typography>
              </Box>
            </Box>

            {/* Desktop Nav */}
            {!isMobile ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.name}
                    component={Link} to={item.path}
                    sx={{
                      color: location.pathname === item.path ? '#1565C0' : '#37474F',
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      px: 2, fontSize: '0.95rem',
                      position: 'relative',
                      '&::after': {
                        content: '""', position: 'absolute',
                        bottom: 8, left: '50%', transform: 'translateX(-50%)',
                        width: location.pathname === item.path ? '60%' : '0%',
                        height: '3px',
                        background: 'linear-gradient(90deg, #0D47A1, #1976D2)',
                        borderRadius: '3px', transition: 'width 0.3s ease',
                      },
                      '&:hover': { color: '#1565C0', bgcolor: 'rgba(13,71,161,0.05)',
                        '&::after': { width: '60%' } },
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
                <Button
                  component={Link} to="/contact"
                  variant="contained"
                  sx={{
                    ml: 2, px: 3, py: 1, fontWeight: 700, borderRadius: '50px',
                    background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                    boxShadow: '0 4px 14px rgba(13,71,161,0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #0A3880 0%, #1565C0 100%)',
                      boxShadow: '0 6px 20px rgba(13,71,161,0.45)', transform: 'translateY(-2px)' },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            ) : (
              <IconButton sx={{ color: '#1565C0' }} onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <List sx={{ width: 260, pt: 4 }}>
          {menuItems.map((item) => (
            <ListItem
              button key={item.name}
              component={Link} to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                py: 2,
                borderLeft: location.pathname === item.path ? '3px solid #1565C0' : '3px solid transparent',
                '&:hover': { bgcolor: 'rgba(13,71,161,0.06)' },
              }}
            >
              <ListItemText
                primary={item.name}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 700 : 400,
                  color: location.pathname === item.path ? '#1565C0' : '#37474F',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
